import { createApi } from '@reduxjs/toolkit/query/react';
import * as v from 'valibot';

import { REACT_APP_BE_API_URL } from '#shared';

import {
  asQueryFnResult,
  fetchWithAuthBaseQuery,
  requestJsonWithAuth,
  validateAndTransformResponse,
} from '#shared/api';

import {
  eventTemplatesResponseSchema,
  injectedEventSchema,
} from './schemas/eventTemplate.schema';

import type { EventTemplate, InjectedEvent } from '../model/types';

const TEMPLATES_PATH = 'api/admin/events/templates';
const INJECT_PATH = 'api/admin/events/inject';

/**
 * The events workbench's injection half (TMDXNC-298).
 *
 * TARGETED AT THIS FE'S OWN BACKEND. These routes only exist on a service running
 * with `CASE_EVENTS_ROLE=echo`, because they fire into the one queue that process
 * owns; using the default base url keeps injection off any other environment.
 *
 * BOTH endpoints use `queryFn` rather than the shared base query, for the same
 * reason: they need `expectedForbidden`. A 403 here is the DESIGNED answer on any
 * service that is not a developer's own, and its body names the variable to set —
 * the base query would swallow that sentence and report a generic access denial
 * plus a false `access_denied` incident to Sentry on every visit. That also means
 * neither endpoint gets the base query's 100-second timeout and abort handling,
 * as `stats.api.ts`'s `getGrantedGroups` does not.
 */
export const adminEventsApi = createApi({
  reducerPath: 'adminEventsApi',
  // Required by createApi, and unreached: every endpoint below is a `queryFn`.
  baseQuery: fetchWithAuthBaseQuery,

  endpoints: build => ({
    getEventTemplates: build.query<EventTemplate[], void>({
      queryFn: () =>
        asQueryFnResult(async () => {
          const templates = await requestJsonWithAuth<
            v.InferInput<typeof eventTemplatesResponseSchema>['data']
          >(`${REACT_APP_BE_API_URL}${TEMPLATES_PATH}`, {
            expectedForbidden: true,
          });

          // The BE returns a bare array; wrap it into the ResponseResult envelope
          // the repo's one validation mechanism expects (same move as
          // adminUsers.api.ts).
          return validateAndTransformResponse(
            eventTemplatesResponseSchema,
            {
              data: templates,
              errors: null,
              expires: null,
              dataGeneratedAt: null,
              errorsGeneratedAt: null,
            },
            value => value?.id,
            'eventTemplates',
          ).data;
        }),
    }),

    /**
     * Fire one template. NOT cache-invalidating, deliberately: the reply means the
     * message reached the queue, and the event it becomes arrives later, over the
     * WS, through `applyDomainEventFrame`. Refetching the feed here would race the
     * pipeline and report a miss on every fire.
     */
    injectEventTemplate: build.mutation<InjectedEvent, string>({
      queryFn: templateId =>
        asQueryFnResult(async () => {
          const injected = await requestJsonWithAuth<unknown>(
            `${REACT_APP_BE_API_URL}${INJECT_PATH}`,
            {
              method: 'POST',
              json: { templateId },
              expectedForbidden: true,
            },
          );

          // `v.parse`, not `validateAndTransformResponse`: DATA-001's machinery
          // grades ITEMS of a response so a bad row can be dropped and the rest
          // kept, and there is no rest here. A reply without an eventId means the
          // fire cannot be reported, so throwing is the right answer.
          return v.parse(injectedEventSchema, injected);
        }),
    }),
  }),
});

export const { useGetEventTemplatesQuery, useInjectEventTemplateMutation } =
  adminEventsApi;
